import { Link, useNavigate } from "react-router-dom";
import CartItemRow from "@/components/feature/CartItemRow";
import { useStore } from "@/store/StoreContext";
import { formatBRL } from "@/lib/format";
import { getFreeShippingProgress, discountLabel } from "@/lib/commerce";
import { commercialConfig } from "@/config/storeConfig";

export default function CartPage() {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeFromCart, clearCart, subtotal, discount, total, cartCount } =
    useStore();

  if (cart.length === 0) {
    return (
      <section className="w-full px-4 py-20 md:px-8 md:py-28">
        <div className="mx-auto flex max-w-lg flex-col items-center text-center">
          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-primary-50 text-primary-600">
            <i className="ri-shopping-bag-3-line text-4xl" />
          </span>
          <h1 className="mt-6 font-heading text-3xl text-foreground-900">Sua sacola está vazia</h1>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            Escolha seus produtos favoritos e volte aqui para finalizar seu pedido.
          </p>
          <Link
            to="/novidades"
            className="mt-7 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            Ver produtos
          </Link>
        </div>
      </section>
    );
  }

  const progress = getFreeShippingProgress(subtotal);

  return (
    <div className="w-full bg-background-50">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-16">
        <nav aria-label="Caminho de navegação" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
          <Link to="/" className="cursor-pointer whitespace-nowrap transition-colors hover:text-primary-600">
            Início
          </Link>
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-arrow-right-s-line" />
          </span>
          <span className="whitespace-nowrap text-foreground-800">Sacola</span>
        </nav>

        <h1 className="mt-5 font-heading text-4xl font-medium text-foreground-900 md:text-5xl">
          Sua sacola
        </h1>
        <p className="mt-3 max-w-xl text-sm text-foreground-600">
          Confira os produtos escolhidos, ajuste o que precisar e siga para a finalização.
        </p>

        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-[1.6fr_1fr] lg:gap-12">
          <div>
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-foreground-700">
                <strong className="font-medium text-foreground-900">{cartCount}</strong>{" "}
                {cartCount === 1 ? "item na sacola" : "itens na sacola"}
              </p>
              <button
                type="button"
                onClick={clearCart}
                className="cursor-pointer whitespace-nowrap text-xs text-foreground-500 underline underline-offset-4 transition-colors hover:text-accent-700"
              >
                Limpar sacola
              </button>
            </div>

            <div className="mt-4 flex flex-col gap-4">
              {cart.map((item) => (
                <CartItemRow
                  key={item.key}
                  item={item}
                  onChangeQuantity={updateQuantity}
                  onRemove={removeFromCart}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={() => navigate("/novidades")}
              className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm text-foreground-600 transition-colors hover:text-primary-600"
            >
              <i className="ri-add-line text-base" />
              Quer continuar escolhendo?
            </button>
          </div>

          <div>
            <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              <h2 className="font-heading text-2xl text-foreground-900">Resumo do pedido</h2>

              <div className="mt-5 flex flex-col gap-2 text-sm text-foreground-700">
                <div className="flex items-center justify-between">
                  <span>Produtos</span>
                  <span>{formatBRL(subtotal)}</span>
                </div>
                {discount > 0 ? (
                  <div className="flex items-center justify-between text-accent-700">
                    <span>Desconto demonstrativo</span>
                    <span>-{formatBRL(discount)}</span>
                  </div>
                ) : null}
                <div className="flex items-center justify-between">
                  <span>Frete</span>
                  <span className="text-foreground-500">Calculado na finalização</span>
                </div>
                <div className="mt-3 flex items-center justify-between border-t border-background-300 pt-3">
                  <span className="font-heading text-xl text-foreground-900">Total estimado</span>
                  <span className="font-heading text-xl text-primary-700">{formatBRL(total)}</span>
                </div>
              </div>

              {progress.hasRule ? (
                <div className="mt-4 rounded-md border border-secondary-200 bg-secondary-50 p-3">
                  <p className="text-xs leading-relaxed text-foreground-700">{progress.message}</p>
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-background-300">
                    <div
                      className="h-full rounded-full bg-secondary-500 transition-all"
                      style={{
                        width: `${Math.min(
                          100,
                          (subtotal / (progress.remaining + subtotal || 1)) * 100,
                        )}%`,
                      }}
                    />
                  </div>
                </div>
              ) : null}

              {commercialConfig.discount.enabled ? (
                <p className="mt-4 text-[11px] leading-relaxed text-foreground-500">{discountLabel}</p>
              ) : null}

              <button
                type="button"
                onClick={() => navigate("/checkout")}
                className="mt-6 inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-arrow-right-line text-lg" />
                Finalizar pedido
              </button>
              <p className="mt-3 text-center text-[11px] leading-relaxed text-foreground-500">
                Você informa os dados, escolhe a entrega e confere tudo antes de enviar pelo
                WhatsApp.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}